import React, { useState } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement,
    LineElement, BarElement, ArcElement, Title, Tooltip, Legend
} from 'chart.js';
import {
    Calendar, Eye, Thermometer, Zap, Droplet, ShieldCheck,
    TrendingUp, AlertTriangle, AlertCircle, FileText, LoaderCircle
} from 'lucide-react';
import '../styles/Dashboard.css';
import { generateUnifiedDashboardPredictionReport } from '../utils/reportGenerator';
import { useAlertFeed } from '../context/AlertContext';

// IMPORT THE HELPER
import { getChartConfigs } from './DBChartLogics';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

export default function Dashboard() {
    // 1. Unified State for Filters
    const [filters, setFilters] = useState({
        chlorine: 'hourly',
        rawWater: 'hourly',
        chemical: 'hourly',
        intake: 'hourly',
        comparativeView: 'daily',
        comparativeMetric: 'all'
    });

    const [charts, setCharts] = useState(null);
    const [chartsError, setChartsError] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [activeLoadingChart, setActiveLoadingChart] = useState(null);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [reportError, setReportError] = useState(null);
    const chartRefs = React.useRef({});
    const { setAlerts } = useAlertFeed();

    // Load chart configs whenever filters change.
    React.useEffect(() => {
        let isMounted = true;

        const loadCharts = async () => {
            try {
                setChartsError(null);
                const nextCharts = await getChartConfigs(filters);
                if (isMounted) {
                    setCharts(nextCharts);
                    setIsInitialLoading(false);
                    setActiveLoadingChart(null);
                }
            } catch (error) {
                if (isMounted) {
                    setChartsError(error?.message || 'Failed to load chart data');
                    setIsInitialLoading(false);
                    setActiveLoadingChart(null);
                }
            }
        };

        loadCharts();

        return () => {
            isMounted = false;
        };
    }, [filters]);

    const isChartsReady = Boolean(charts);
    const kpiData = charts?.kpiData || {
        rawTurbidity: { value: '5.2', unit: 'NTU', subText: '+0.3 from last hour', subColor: 'text-red' },
        rawWaterPh: { value: '7.1', unit: '', subText: 'Safe Range (6.5-8.5)', subColor: 'text-green' },
        conductivity: { value: '302', unit: 'µS/cm', subText: 'Stable', subColor: 'text-gray' },
        chlorineDosage: { value: '15.8', unit: 'kg/h', recommended: '16.1' }
    };
    const wqiMetrics = charts?.wqiMetrics || {
        value: 92,
        max: 100,
        status: 'SAFE',
        details: {
            finalTurbidity: '0.3 NTU',
            finalPH: '7.0',
            chlorineResidual: '1.2 ppm'
        }
    };
    const finalQualityData = charts?.finalQualityData || {
        status: 'SAFE',
        wqiText: '92/100'
    };
    const comparativeData = charts?.comparativeData || {
        todayVsYesterday: {
            todayLabel: 'Today',
            yesterdayLabel: 'Yesterday',
            metrics: [
                { name: 'Turbidity', today: '5.1 NTU', yesterday: '4.8 NTU' },
                { name: 'pH', today: '7.1', yesterday: '7.0' },
                { name: 'Conductivity', today: '302 µS/cm', yesterday: '298 µS/cm' }
            ]
        },
        hourVsPrevious: {
            currentHourLabel: 'Last Hour',
            previousHourLabel: 'Previous Hour',
            metrics: [
                { name: 'Turbidity', current: '5.1 NTU', previous: '4.9 NTU' },
                { name: 'pH', current: '7.1', previous: '7.0' },
                { name: 'Conductivity', current: '302 µS/cm', previous: '299 µS/cm' }
            ]
        }
    };
    const alertsData = React.useMemo(
        () => (Array.isArray(charts?.alertsData) ? charts.alertsData : []),
        [charts?.alertsData]
    );
    const safeChlorineData = charts?.chlorineData || { labels: [], datasets: [] };
    const safeRawWaterData = charts?.rawWaterData || { labels: [], datasets: [] };
    const safeChemicalData = charts?.chemicalData || { labels: [], datasets: [] };
    const safeIntakeData = charts?.intakeData || { labels: [], datasets: [] };
    const safeWqiData = charts?.wqiData || {
        labels: ['WQI', 'Remaining'],
        datasets: [{ data: [0, 100], backgroundColor: ['#1e40af', '#e2e8f0'], borderWidth: 0 }]
    };

    const getComparativeMetrics = () => {
        const source = filters.comparativeView === 'daily'
            ? comparativeData.todayVsYesterday.metrics
            : comparativeData.hourVsPrevious.metrics;

        if (filters.comparativeMetric === 'all') {
            return source;
        }

        return source.filter((item) => item.name.toLowerCase() === filters.comparativeMetric);
    };

    const comparativeMetrics = getComparativeMetrics();

    const lastHourWindow = React.useMemo(() => {
        const now = new Date();
        const previousHour = new Date(now.getTime() - (60 * 60 * 1000));

        return {
            date: previousHour.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }),
            range: `${previousHour.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
        };
    }, []);

    React.useEffect(() => {
        setAlerts(alertsData);
    }, [alertsData, setAlerts]);

    if (isInitialLoading && !isChartsReady && !chartsError) {
        return (
            <div className="dashboard-page dashboard-loader-page">
                <div className="dashboard-loader-card" role="status" aria-live="polite">
                    <div className="dashboard-spinner" aria-hidden="true" />
                    <h2 className="dashboard-loader-title">Loading Dashboard</h2>
                    <p className="dashboard-loader-subtitle">Fetching latest water quality and chlorine analytics...</p>
                </div>
            </div>
        );
    }

    // --- Handlers ---
    const handleFilterChange = (key, value) => {
        setActiveLoadingChart(key);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleGenerateReport = async () => {
        if (isGeneratingReport) return;

        setReportError(null);
        setIsGeneratingReport(true);

        const getChartImage = (key) => {
            const chart = chartRefs.current[key];
            if (!chart || typeof chart.toBase64Image !== 'function') {
                return null;
            }

            try {
                return chart.toBase64Image();
            } catch {
                return null;
            }
        };

        const dashboardSnapshot = {
            filters,
            kpiData,
            wqiMetrics,
            finalQualityData,
            comparativeData,
            alertsData,
            chartImages: {
                chlorine: getChartImage('chlorine'),
                rawWater: getChartImage('rawWater'),
                chemical: getChartImage('chemical'),
                intake: getChartImage('intake'),
                wqi: getChartImage('wqi'),
            },
            chartData: {
                chlorine: safeChlorineData,
                rawWater: safeRawWaterData,
                chemical: safeChemicalData,
                intake: safeIntakeData,
                wqi: safeWqiData,
            },
        };

        try {
            await generateUnifiedDashboardPredictionReport({
                dashboardSnapshot,
                selectedYear: new Date().getFullYear(),
            });
        } catch (error) {
            setReportError(error?.message || 'Report generation failed');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-container">

                {/* --- Header (REVERTED TO ORIGINAL) --- */}
                <header className="dashboard-header">
                    <div>
                        <h1 className="header-title">Operations Dashboard</h1>
                        <p className="header-subtitle">Chlorine Demand & Water Quality Analysis</p>
                    </div>

                    <div className="last-hour-card">
                        <div className="last-hour-title-row">
                            <Calendar className="icon text-gray" />
                            <span className="last-hour-title">Last hour operational data</span>
                        </div>
                        <div className="last-hour-meta">
                            <span>{lastHourWindow.date}</span>
                            <span>{lastHourWindow.range}</span>
                        </div>
                    </div>
                </header>

                {/* --- KPI Grid --- */}
                <section className="kpi-grid">
                    <KpiCard title="Raw Turbidity" value={kpiData.rawTurbidity.value} unit={kpiData.rawTurbidity.unit} icon={<Eye className="icon" />} subText={kpiData.rawTurbidity.subText} subColor={kpiData.rawTurbidity.subColor} />
                    <KpiCard title="Raw Water pH" value={kpiData.rawWaterPh.value} unit={kpiData.rawWaterPh.unit} icon={<Thermometer className="icon" />} subText={kpiData.rawWaterPh.subText} subColor={kpiData.rawWaterPh.subColor} />
                    <KpiCard title="Conductivity" value={kpiData.conductivity.value} unit={kpiData.conductivity.unit} icon={<Zap className="icon" />} subText={kpiData.conductivity.subText} subColor={kpiData.conductivity.subColor} />

                    <div className="kpi-card highlight-blue">
                        <div className="kpi-header">
                            <span className="kpi-title-text" style={{ color: 'var(--primary-blue)' }}>Chlorine Dosage</span>
                            <Droplet className="icon" style={{ color: 'var(--primary-blue)' }} />
                        </div>
                        <div className="kpi-value">{kpiData.chlorineDosage.value} <span className="text-lg">{kpiData.chlorineDosage.unit}</span></div>
                        <div className="kpi-subtext" style={{ color: 'var(--primary-blue-light)' }}>
                            Rec: <span style={{ fontWeight: 700, marginLeft: '4px' }}>{kpiData.chlorineDosage.recommended} {kpiData.chlorineDosage.unit}</span>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-header">
                            <span className="kpi-title-text">Final Quality</span>
                            <ShieldCheck className="icon" style={{ color: 'var(--primary-blue-light)' }} />
                        </div>
                        <div className="kpi-value" style={{ color: finalQualityData.status === 'SAFE' ? 'var(--success-green)' : '#b42318' }}>{finalQualityData.status}</div>
                        <div className="kpi-subtext" style={{ color: 'var(--primary-blue-light)' }}>WQI: <span style={{ fontWeight: 700, color: 'var(--primary-blue)' }}>{finalQualityData.wqiText}</span></div>
                    </div>
                </section>

                {/* --- Main Content --- */}
                <main className="content-grid">

                    {/* Charts Column */}
                    <div className="col-chart-section" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                        <ChartCard title="Chlorine Dosage: Actual vs. ML Forecast" filter={filters.chlorine} onChange={(e) => handleFilterChange('chlorine', e.target.value)} heightClass="h-tall">
                            {isChartsReady && activeLoadingChart !== 'chlorine' ? (
                                <Line
                                    ref={(chart) => {
                                        chartRefs.current.chlorine = chart;
                                    }}
                                    data={safeChlorineData}
                                    options={{ responsive: true, maintainAspectRatio: false }}
                                />
                            ) : (
                                <div className="chart-loading-message">
                                    <div className="chart-spinner" aria-hidden="true" />
                                    <span>Updating chart...</span>
                                </div>
                            )}
                        </ChartCard>

                        <ChartCard title="Raw Water Quality Trends" filter={filters.rawWater} onChange={(e) => handleFilterChange('rawWater', e.target.value)} heightClass="h-medium">
                            {isChartsReady && activeLoadingChart !== 'rawWater' ? (
                                <Line
                                    ref={(chart) => {
                                        chartRefs.current.rawWater = chart;
                                    }}
                                    data={safeRawWaterData}
                                    options={{ responsive: true, maintainAspectRatio: false, scales: { y: { position: 'left' }, y1: { position: 'right', grid: { drawOnChartArea: false } } } }}
                                />
                            ) : (
                                <div className="chart-loading-message">
                                    <div className="chart-spinner" aria-hidden="true" />
                                    <span>Updating chart...</span>
                                </div>
                            )}
                        </ChartCard>

                        <ChartCard title="Other Chemical Dosage (ppm)" filter={filters.chemical} onChange={(e) => handleFilterChange('chemical', e.target.value)} heightClass="h-medium">
                            {isChartsReady && activeLoadingChart !== 'chemical' ? (
                                <Line
                                    ref={(chart) => {
                                        chartRefs.current.chemical = chart;
                                    }}
                                    data={safeChemicalData}
                                    options={{ responsive: true, maintainAspectRatio: false }}
                                />
                            ) : (
                                <div className="chart-loading-message">
                                    <div className="chart-spinner" aria-hidden="true" />
                                    <span>Updating chart...</span>
                                </div>
                            )}
                        </ChartCard>

                        {/* Analysis Section */}
                        <section className="analysis-row analysis-single">
                            <div className="analysis-card">
                                <div className="chart-header" style={{ marginBottom: '0.75rem' }}>
                                    <h3 className="analysis-title" style={{ marginBottom: 0 }}>Comparative Analysis</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <select
                                            value={filters.comparativeView}
                                            onChange={(e) => handleFilterChange('comparativeView', e.target.value)}
                                            className="chart-select"
                                        >
                                            <option value="daily">Today vs Yesterday</option>
                                            <option value="hourly">Last Hour vs Previous</option>
                                        </select>
                                        <select
                                            value={filters.comparativeMetric}
                                            onChange={(e) => handleFilterChange('comparativeMetric', e.target.value)}
                                            className="chart-select"
                                        >
                                            <option value="all">All Metrics</option>
                                            <option value="turbidity">Turbidity</option>
                                            <option value="ph">pH</option>
                                            <option value="conductivity">Conductivity</option>
                                        </select>
                                    </div>
                                </div>

                                {isChartsReady && activeLoadingChart !== 'comparativeView' && activeLoadingChart !== 'comparativeMetric' ? (
                                    <>
                                        <p className="kpi-title-text" style={{ marginBottom: '0.5rem' }}>
                                            {filters.comparativeView === 'daily'
                                                ? `${comparativeData.todayVsYesterday.todayLabel} vs ${comparativeData.todayVsYesterday.yesterdayLabel}`
                                                : `${comparativeData.hourVsPrevious.currentHourLabel} vs ${comparativeData.hourVsPrevious.previousHourLabel}`}
                                        </p>
                                        <ul className="comp-list">
                                            {comparativeMetrics.map((item) => (
                                                <li className="comp-item" key={`${filters.comparativeView}-${item.name}`}>
                                                    <span className="comp-label">{item.name}</span>
                                                    <span className="comp-val">
                                                        {filters.comparativeView === 'daily'
                                                            ? `${comparativeData.todayVsYesterday.todayLabel}: ${item.today} | ${comparativeData.todayVsYesterday.yesterdayLabel}: ${item.yesterday}`
                                                            : `${comparativeData.hourVsPrevious.currentHourLabel}: ${item.current} | ${comparativeData.hourVsPrevious.previousHourLabel}: ${item.previous}`}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                        {comparativeMetrics.length === 0 && (
                                            <p style={{ margin: 0, color: 'var(--primary-blue-light)' }}>No comparison data available.</p>
                                        )}
                                        <div className="comp-trend"><TrendingUp className="icon"/> Filtered comparison view</div>
                                    </>
                                ) : (
                                    <div className="chart-loading-message" style={{ minHeight: '10rem' }}>
                                        <div className="chart-spinner" aria-hidden="true" />
                                        <span>Updating comparative analysis...</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Sidebar Column */}
                    <div className="col-sidebar-section" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                        <div className="kpi-card wqi-card-container">
                            <h2 className="chart-title">Final Water Quality Index (WQI)</h2>
                            <div className="gauge-section">
                                {isChartsReady ? (
                                    <Doughnut
                                        ref={(chart) => {
                                            chartRefs.current.wqi = chart;
                                        }}
                                        data={safeWqiData}
                                        options={{ cutout: '75%', plugins: { legend: { display: false }, tooltip: { enabled: false } } }}
                                    />
                                ) : (
                                    <div className="chart-loading-message">
                                        <div className="chart-spinner" aria-hidden="true" />
                                        <span>Loading chart data...</span>
                                    </div>
                                )}
                                <div className="gauge-overlay">
                                    <span className="gauge-value">{wqiMetrics.value}</span>
                                    <span className="gauge-max">/ {wqiMetrics.max}</span>
                                </div>
                            </div>

                            <div className="wqi-details">
                                <div className="wqi-row"><span className="wqi-label">Final Turbidity</span><span className="wqi-val">{wqiMetrics.details.finalTurbidity}</span></div>
                                <div className="wqi-row"><span className="wqi-label">Final pH</span><span className="wqi-val">{wqiMetrics.details.finalPH}</span></div>
                                <div className="wqi-row"><span className="wqi-label">Chlorine Residual</span><span className="wqi-val">{wqiMetrics.details.chlorineResidual}</span></div>
                            </div>
                        </div>

                        <ChartCard title="Intake Flow" filter={filters.intake} onChange={(e) => handleFilterChange('intake', e.target.value)} heightClass="h-auto">
                            <div style={{ height: '12rem' }}>
                                {isChartsReady ? (
                                    activeLoadingChart !== 'intake' ? (
                                        <Bar
                                            ref={(chart) => {
                                                chartRefs.current.intake = chart;
                                            }}
                                            data={safeIntakeData}
                                            options={{ responsive: true, maintainAspectRatio: false }}
                                        />
                                    ) : (
                                        <div className="chart-loading-message">
                                            <div className="chart-spinner" aria-hidden="true" />
                                            <span>Updating chart...</span>
                                        </div>
                                    )
                                ) : (
                                    <div className="chart-loading-message">
                                        <div className="chart-spinner" aria-hidden="true" />
                                        <span>Loading chart data...</span>
                                    </div>
                                )}
                            </div>
                            <div className="flow-footer">
                                <span className="text-gray" style={{ fontSize: '0.875rem' }}>Daily Total</span>
                                <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>28,500 m³</span>
                            </div>
                        </ChartCard>

                        {chartsError && (
                            <div className="chart-card h-auto">
                                <h2 className="chart-title">Chart Error</h2>
                                <p style={{ color: '#b42318' }}>{chartsError}</p>
                            </div>
                        )}

                        <div className="chart-card h-auto">
                            <h2 className="chart-title" style={{ marginBottom: '1rem' }}>Alerts Log</h2>
                            {alertsData.length === 0 ? (
                                <p style={{ margin: 0, color: 'var(--primary-blue-light)' }}>No alerts in the last hour.</p>
                            ) : (
                                <ul className="alerts-list">
                                    {alertsData.map((alert, index) => (
                                        <AlertItem key={`${alert.title}-${index}`} type={alert.type} title={alert.title} desc={alert.desc} />
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                </main>
            </div>

            <button
                type="button"
                className="report-fab"
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
                aria-label="Generate full report"
                title="Generate full dashboard and prediction report"
            >
                {isGeneratingReport ? (
                    <>
                        <LoaderCircle className="icon report-fab-spin" />
                        Generating...
                    </>
                ) : (
                    <>
                        <FileText className="icon" />
                        Generate Full Report
                    </>
                )}
            </button>

            {reportError && (
                <p className="report-fab-error" role="alert">
                    {reportError}
                </p>
            )}
        </div>
    );
}

// Sub-Components
function KpiCard({ title, value, unit, icon, subText, subColor }) {
    return (
        <div className="kpi-card">
            <div className="kpi-header"><span className="kpi-title-text">{title}</span><span style={{ color: 'var(--primary-blue-light)' }}>{icon}</span></div>
            <div className="kpi-value">{value} <span className="kpi-unit">{unit}</span></div>
            <div className={`kpi-subtext ${subColor}`}><span className="kpi-dot">●</span> {subText}</div>
        </div>
    );
}

function ChartCard({ title, children, filter, onChange, heightClass = "h-medium" }) {
    return (
        <div className={`chart-card ${heightClass}`}>
            <div className="chart-header">
                <h2 className="chart-title">{title}</h2>
                <select value={filter} onChange={onChange} className="chart-select"><option value="hourly">Hourly</option><option value="daily">Daily</option></select>
            </div>
            <div className="chart-wrapper">{children}</div>
        </div>
    );
}

function AlertItem({ type, title, desc }) {
    const cssClass = type === 'red' ? 'alert-red' : 'alert-yellow';
    const Icon = type === 'red' ? AlertTriangle : AlertCircle;
    return (
        <li className={`alert-item ${cssClass}`}>
            <Icon className="icon" style={{ flexShrink: 0 }} />
            <div className="alert-content"><span className="alert-title">{title}</span><span className="alert-desc">{desc}</span></div>
        </li>
    );
}
